import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  IsActiveMatchOptions,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { NavMenuItem } from '@siat-fe/core';
import { filter } from 'rxjs';

@Component({
  selector: 'siat-sidenav-menu-item',
   
  imports: [
    CommonModule,
    RouterModule,
    MatRippleModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslatePipe,
  ],
  templateUrl: './sidenav-menu.component.html',
  styleUrls: ['./sidenav-menu.component.scss'],
})
export class SidenavMenuComponent {
  /**
   * The menu item to render.
   */
  @Input()
  item!: NavMenuItem;

  /**
   * A flag telling if the menu is collapsed.
   */
  @Input()
  isCollapsed = true;

  /**
   * Emitted when the item is clicked.
   */
  @Output()
  itemClicked = new EventEmitter<NavMenuItem>();

  /**
   * The router service.
   */
  readonly router = inject(Router);

  /**
   * Emits once per completed navigation, used to recompute {@link isItemActive}
   * reactively. `Router.isActive` is an imperative check with no signal of its
   * own, so without this, an item's highlight would only refresh on the next
   * unrelated CD-triggering event (e.g. mouse hover) instead of right after
   * navigation completes.
   */
  private readonly navigationEnd = toSignal(
    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
  );

  /**
   * The options to use when checking if the item is active.
   */
  private readonly subsetMatchOptions: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'subset',
    matrixParams: 'ignored',
    fragment: 'ignored',
  };

  /**
   * The options to use when checking if the item is active.
   */
  private readonly exactMatchOptions: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'exact',
    matrixParams: 'ignored',
    fragment: 'ignored',
  };

  /**
   * True if the item or one of its children is active. Recomputes whenever a
   * navigation completes (see {@link navigationEnd}).
   */
  isItemActive = computed(() => {
    this.navigationEnd();

    if (this.item.subItems && this.item.subItems.length > 0) {
      return this.item.subItems.some(subItem => {
        if (!subItem.href || subItem.href === '') {
          return false;
        }
        return this.router.isActive(subItem.href, this.subsetMatchOptions);
      });
    }

    if (this.item.href) {
      const isModuleRoot = this.item.href === '/amministrazione';
      return this.router.isActive(
        this.item.href,
        isModuleRoot ? this.exactMatchOptions : this.subsetMatchOptions
      );
    }

    return false;
  });

  /**
   * Id of the visible caption labelling this nav item, referenced via
   * aria-labelledby so the accessible name always matches the visible text
   * instead of duplicating it in a separate, hidden aria-label.
   * @returns {string} The caption element id.
   */
  get labelId(): string {
    const base = this.item?.href || this.item?.title || 'item';
    return 'sidenav-label-' + base.replace(/[^a-zA-Z0-9]+/g, '-');
  }

  /**
   * Handles item click by emitting an event to the parent component.
   */
  onItemClick(): void {
    this.itemClicked.emit(this.item);
  }
}
